import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Handshake, HeadphonesIcon, ShieldCheck, Sparkles, TrendingUp, Users, Wallet } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { PUBLIC_IMAGES } from '../lib/publicImages';
import { EditorialImageCard } from '../components/EditorialImageCard';
import { toast } from '../components/ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusBadge = 'rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[10px] uppercase tracking-[0.16em] text-primary';

const PartnersPage = () => {
  const [formData, setFormData] = useState({ nombre: '', email: '', empresa: '', country: '', web: '', tipoPartner: '', mensaje: '' });
  const [catalog, setCatalog] = useState([]);
  const [marketShift, setMarketShift] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [legalState, setLegalState] = useState({ open: false, plan: null, accepted: false, processing: false, email: '', full_name: '', company_name: '', phone: '' });

  useEffect(() => {
    const loadCatalog = async () => {
      try {
        const res = await fetch(`${API}/partner/plan-catalog`);
        const data = await res.json();
        setCatalog(data.plans || []);
        setMarketShift(data.market_shift || null);
      } catch (error) {
        console.error(error);
      }
    };
    loadCatalog();
  }, []);

  const benefits = useMemo(() => [
    { icon: Wallet, title: 'Comisiones con estructura', desc: 'No es recomendar por recomendar: es vender con CRM, onboarding y seguimiento real.' },
    { icon: TrendingUp, title: 'Ingresos recurrentes', desc: 'Combina comisión de cierre, continuidad mensual y expansión de red dentro del ecosistema.' },
    { icon: HeadphonesIcon, title: 'Infraestructura lista', desc: 'Contrato, checkout, panel partner, playbooks y activación comercial ya preparados.' },
    { icon: Users, title: 'Oferta fácil de vender', desc: 'Te ayudas de una propuesta más clara para agencias, consultores, closers e integradores.' },
  ], []);

  const partnerTypes = useMemo(() => [
    { value: 'comercial', label: 'Partner Comercial', desc: 'Para quien quiere vender soluciones IA y generar comisiones.' },
    { value: 'integrador', label: 'Partner Integrador', desc: 'Para quien además coordina activación, onboarding y continuidad.' },
    { value: 'estrategico', label: 'Partner Estratégico', desc: 'Para agencias, operadores y líderes de red con ambición de escala.' }
  ], []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API}/partners`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (!response.ok) throw new Error('No se pudo enviar la solicitud');
      setSubmitted(true);
      setFormData({ nombre: '', email: '', empresa: '', country: '', web: '', tipoPartner: '', mensaje: '' });
    } catch (error) {
      toast.error(error.message || 'Error al enviar la solicitud');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openLegal = (plan) => {
    setLegalState({ open: true, plan, accepted: false, processing: false, email: '', full_name: '', company_name: '', phone: '' });
  };

  const closeLegal = () => setLegalState({ open: false, plan: null, accepted: false, processing: false, email: '', full_name: '', company_name: '', phone: '' });

  const handlePartnerCheckout = async () => {
    if (!legalState.plan || !legalState.accepted || !legalState.email || !legalState.phone) {
      toast.error('Completa y acepta el contrato antes de continuar');
      return;
    }
    try {
      setLegalState((prev) => ({ ...prev, processing: true }));
      const acceptanceRes = await fetch(`${API}/partner/legal-acceptance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan_id: legalState.plan.id,
          email: legalState.email,
          full_name: legalState.full_name,
          company_name: legalState.company_name,
          phone: legalState.phone,
          accepted: true,
          contract_version: legalState.plan.contract?.version || 'partner_terms_v1',
        })
      });
      const acceptanceData = await acceptanceRes.json();
      if (!acceptanceRes.ok) throw new Error(acceptanceData.detail || 'No se pudo registrar la aceptación legal');

      const checkoutRes = await fetch(`${API}/partner/subscription-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan_id: legalState.plan.id,
          origin_url: window.location.origin,
          email: legalState.email,
          acceptance_id: acceptanceData.acceptance.id,
          full_name: legalState.full_name,
          company_name: legalState.company_name,
          phone: legalState.phone,
          return_path: '/partners',
        })
      });
      const checkoutData = await checkoutRes.json();
      if (!checkoutRes.ok) throw new Error(checkoutData.detail || 'No se pudo iniciar el checkout');
      window.location.href = checkoutData.url;
    } catch (error) {
      toast.error(error.message || 'No se pudo iniciar el checkout');
    } finally {
      setLegalState((prev) => ({ ...prev, processing: false }));
    }
  };

  return (
    <div className="min-h-screen bg-background pt-24 pb-golden-2xl">
      <SEO title="Neuralia Partner | Nueva línea de ingresos" description="Programa partner para agencias, consultores, closers e integradores que quieren vender Neuralia con estructura real." path="/partners" />

      <section className="relative overflow-hidden py-golden-2xl">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-golden-md lg:px-golden-lg relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-golden-lg">
              <Handshake className="w-4 h-4 text-primary" />
              <span className="text-sm text-primary font-medium">Neuralia Partner</span>
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-golden-lg">Abre una <span className="gradient-text">línea de ingresos recurrentes</span> vendiendo Neuralia con estructura real</motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-xl text-text-body">Neuralia Partner está pensado para agencias, consultores, closers, integradores y operadores comerciales que quieren cerrar mejor, activar mejor y quedarse en la continuidad del cliente.</motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="flex flex-wrap justify-center gap-3 mt-golden-lg">
              <button
                type="button"
                className="btn-primary"
                data-testid="partner-pricing-cta"
                onClick={() => document.getElementById('partner-plan-grid')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              >
                Ver planes Partner
              </button>
              <Link to="/acceso-partners" className="btn-secondary" data-testid="partner-access-cta">Acceso partners</Link>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-golden-xl">
        <div className="max-w-7xl mx-auto px-golden-md lg:px-golden-lg grid gap-golden-lg md:grid-cols-2 xl:grid-cols-4">
          {benefits.map((benefit, index) => (
            <motion.div key={benefit.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.08 }} className="glass-card p-golden-lg" data-testid={`partner-benefit-${index}`}>
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-golden-md"><benefit.icon className="w-6 h-6 text-primary" /></div>
              <h3 className="text-lg mb-golden-sm">{benefit.title}</h3>
              <p className="text-text-body text-sm">{benefit.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {marketShift && (
        <section className="py-golden-xl">
          <div className="max-w-7xl mx-auto px-golden-md lg:px-golden-lg">
            <div className="glass-card tracing-panel p-golden-xl" data-testid="partner-market-shift-card">
              <p className="editorial-kicker mb-golden-md">Oportunidad partner</p>
              <h2 className="mb-golden-md">{marketShift.headline}</h2>
              <p className="text-text-body text-base max-w-4xl">{marketShift.summary}</p>
              <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-golden-lg mt-golden-xl">
                {marketShift.signals?.map((signal, index) => <div key={index} className="rounded-2xl border border-border-subtle bg-card/50 p-5 text-sm text-text-body" data-testid={`partner-market-signal-${index}`}>{signal}</div>)}
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="py-golden-xl" id="partner-plan-grid">
        <div className="max-w-7xl mx-auto px-golden-md lg:px-golden-lg">
          <div className="text-center mb-golden-xl">
            <h2 className="mb-golden-md">Planes <span className="gradient-text">Partner</span> listos para comprar</h2>
            <p className="text-text-body max-w-3xl mx-auto">Tres niveles claros para empezar, escalar y operar con más cobertura. Cada plan muestra precio, comisiones, capacidad operativa y lo que incluye. Antes del pago, el partner acepta el contrato completo del servicio.</p>
          </div>
          <div className="grid xl:grid-cols-3 md:grid-cols-2 gap-golden-lg" data-testid="partner-plan-grid">
            {catalog.map((plan, index) => (
              <motion.div key={plan.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.08 }} className="glass-card p-golden-lg flex flex-col" data-testid={`partner-plan-card-${plan.id}`}>
                <div className="flex items-center justify-between gap-3 mb-golden-md">
                  <span className={statusBadge}>Plan {plan.label}</span>
                  <span className="text-primary font-semibold">${plan.price_usd}/mes</span>
                </div>
                <h3 className="text-2xl mb-golden-sm">{plan.label}</h3>
                <p className="text-primary text-sm font-medium mb-golden-sm">{plan.slogan}</p>
                <p className="text-text-body text-sm mb-golden-md">{plan.credits_value_copy}</p>
                <div className="space-y-2 mb-golden-md">
                  <div className="text-xs uppercase tracking-[0.18em] text-text-muted">Qué incluye</div>
                  {plan.what_includes?.map((item) => <div key={item} className="flex items-start gap-2 text-sm text-text-body"><CheckCircle className="w-4 h-4 mt-0.5 text-primary" /><span>{item}</span></div>)}
                </div>
                <div className="rounded-2xl border border-border-subtle bg-background/55 p-4 mb-golden-md" data-testid={`partner-plan-commission-box-${plan.id}`}>
                  <p className="text-xs uppercase tracking-[0.18em] text-text-muted mb-2">Comisiones y red</p>
                  <p className="text-sm text-text-body">{plan.commissions_copy}</p>
                </div>
                <div className="rounded-2xl border border-border-subtle bg-background/55 p-4 mb-golden-md">
                  <p className="text-xs uppercase tracking-[0.18em] text-text-muted mb-2">Capacidad del plan</p>
                  <p className="text-sm text-text-body">Hasta {plan.agent_limit} agentes activos · {(plan.credit_limit || 0)} créditos operativos al año · canales: {(plan.channels || []).join(', ')}</p>
                </div>
                <button type="button" onClick={() => openLegal(plan)} className="btn-primary mt-auto justify-center" data-testid={`partner-buy-button-${plan.id}`}>Comprar directo</button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-golden-xl bg-gradient-to-b from-transparent to-primary/5">
        <div className="max-w-7xl mx-auto px-golden-md lg:px-golden-lg">
          <h2 className="text-center mb-golden-xl">Tipos de <span className="gradient-text">Partner</span></h2>
          <div className="grid md:grid-cols-3 gap-golden-lg">
            {partnerTypes.map((type, index) => (
              <motion.div key={type.value} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="glass-card p-golden-xl text-center" data-testid={`partner-type-${type.value}`}>
                <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-golden-lg"><Sparkles className="w-8 h-8 text-primary" /></div>
                <h3 className="text-xl mb-golden-sm">{type.label}</h3>
                <p className="text-text-body">{type.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-golden-xl">
            <EditorialImageCard
              image={PUBLIC_IMAGES.partners.benefits}
              alt="Socios comerciales revisando crecimiento y oportunidades"
              kicker="Canal comercial con estructura"
              title="Nada de humo: te damos una oferta partner más clara, vendible y sostenible"
              description="Neuralia Partner es una capa comercial y operativa para agencias, consultores, integradores y equipos de venta que quieren ofrecer soluciones útiles, bien presentadas y con continuidad real."
              testId="partners-editorial-image"
              minHeight="min-h-[23rem]"
            />
          </div>
        </div>
      </section>

      <section className="py-golden-2xl">
        <div className="max-w-2xl mx-auto px-golden-md lg:px-golden-lg">
          <div className="text-center mb-golden-xl">
            <h2 className="mb-golden-md">Solicita <span className="gradient-text">acceso</span></h2>
            <p className="text-text-body">Si todavía no quieres comprar, aplica y te ayudamos a elegir el nivel partner más rentable para tu caso.</p>
          </div>
          {submitted ? (
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="glass-card p-golden-2xl text-center">
              <div className="w-20 h-20 mx-auto mb-golden-lg rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center"><CheckCircle className="w-10 h-10 text-primary" /></div>
              <h3 className="text-2xl mb-golden-md">Solicitud recibida</h3>
              <p className="text-text-body">Revisaremos tu perfil y te contactaremos pronto.</p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="glass-card p-golden-xl space-y-golden-lg" data-testid="partner-form">
              <div className="grid md:grid-cols-2 gap-golden-lg">
                <div><label className="block text-text-body mb-golden-sm text-sm">Nombre *</label><input type="text" className="input-kinesthetic" value={formData.nombre} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} required data-testid="partner-nombre" /></div>
                <div><label className="block text-text-body mb-golden-sm text-sm">Email *</label><input type="email" className="input-kinesthetic" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="partner-email" /></div>
              </div>
              <div className="grid md:grid-cols-2 gap-golden-lg">
                <div><label className="block text-text-body mb-golden-sm text-sm">Empresa</label><input type="text" className="input-kinesthetic" value={formData.empresa} onChange={(e) => setFormData({ ...formData, empresa: e.target.value })} data-testid="partner-empresa" /></div>
                <div><label className="block text-text-body mb-golden-sm text-sm">País</label><input type="text" className="input-kinesthetic" placeholder="Ej. España, México, Colombia" value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} data-testid="partner-country" /></div>
              </div>
              <div><label className="block text-text-body mb-golden-sm text-sm">Tipo de Partner deseado *</label><select className="input-kinesthetic" value={formData.tipoPartner} onChange={(e) => setFormData({ ...formData, tipoPartner: e.target.value })} required data-testid="partner-tipo"><option value="">Selecciona una opción</option>{partnerTypes.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}</select></div>
              <div><label className="block text-text-body mb-golden-sm text-sm">¿Qué quieres vender o activar como partner?</label><textarea className="input-kinesthetic min-h-[120px] resize-none" value={formData.mensaje} onChange={(e) => setFormData({ ...formData, mensaje: e.target.value })} placeholder="Cuéntanos tu tipo de cartera, sector y cómo te gustaría monetizar Neuralia..." data-testid="partner-mensaje" /></div>
              <button type="submit" className="btn-primary w-full justify-center" disabled={isSubmitting} data-testid="partner-submit">{isSubmitting ? 'Enviando...' : 'Solicitar acceso partner'}</button>
            </form>
          )}
        </div>
      </section>

      {legalState.open && legalState.plan && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4" data-testid="partner-legal-modal-overlay">
          <div className="glass-card w-full max-w-4xl max-h-[90vh] overflow-auto p-6" data-testid="partner-legal-modal">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-text-muted">Contrato legal previo al pago</p>
                <h3 className="text-2xl font-semibold text-text-heading mt-2">{legalState.plan.contract?.title}</h3>
                <p className="text-sm text-text-body mt-2">{legalState.plan.contract?.summary}</p>
              </div>
              <button type="button" onClick={closeLegal} className="btn-secondary" data-testid="partner-legal-close-button">Cerrar</button>
            </div>

            <div className="grid xl:grid-cols-[1.05fr_0.95fr] gap-6">
              <div className="space-y-4">
                <div className="rounded-2xl border border-border-subtle bg-background/55 p-4" data-testid="partner-legal-plan-summary-box">
                  <p className="text-xs uppercase tracking-[0.18em] text-text-muted mb-2">Plan elegido</p>
                  <p className="text-text-heading font-semibold">{legalState.plan.label} · ${legalState.plan.price_usd}/mes</p>
                  <p className="text-sm text-text-body mt-2">{legalState.plan.slogan}</p>
                </div>

                <div className="rounded-2xl border border-border-subtle bg-background/55 p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-text-muted mb-2">Cláusulas principales</p>
                  <div className="space-y-3 text-sm text-text-body" data-testid="partner-legal-clauses-list">
                    {(legalState.plan.contract?.clauses || []).map((clause, index) => <div key={index}>• {clause}</div>)}
                  </div>
                </div>

                <div className="rounded-2xl border border-border-subtle bg-background/55 p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-text-muted mb-2">Aceptación</p>
                  <div className="space-y-3 text-sm text-text-body">
                    {(legalState.plan.contract?.acceptance_checklist || []).map((item, index) => <div key={index}>• {item}</div>)}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div><label className="block text-text-body mb-2 text-sm">Nombre completo *</label><input className="input-kinesthetic" value={legalState.full_name} onChange={(e) => setLegalState((prev) => ({ ...prev, full_name: e.target.value }))} data-testid="partner-legal-full-name-input" /></div>
                <div><label className="block text-text-body mb-2 text-sm">Email *</label><input type="email" className="input-kinesthetic" value={legalState.email} onChange={(e) => setLegalState((prev) => ({ ...prev, email: e.target.value }))} data-testid="partner-legal-email-input" /></div>
                <div><label className="block text-text-body mb-2 text-sm">Empresa / marca</label><input className="input-kinesthetic" value={legalState.company_name} onChange={(e) => setLegalState((prev) => ({ ...prev, company_name: e.target.value }))} data-testid="partner-legal-company-input" /></div>
                <div><label className="block text-text-body mb-2 text-sm">Teléfono / WhatsApp *</label><input className="input-kinesthetic" placeholder="Ej. +34 600 000 000" value={legalState.phone} onChange={(e) => setLegalState((prev) => ({ ...prev, phone: e.target.value }))} data-testid="partner-legal-phone-input" /></div>
                <label className="flex items-start gap-3 rounded-2xl border border-border-subtle bg-background/55 p-4 text-sm text-text-body" data-testid="partner-legal-acceptance-box">
                  <input type="checkbox" checked={legalState.accepted} onChange={(e) => setLegalState((prev) => ({ ...prev, accepted: e.target.checked }))} data-testid="partner-legal-accept-checkbox" />
                  <span>He leído y acepto el contrato del plan, sus límites de uso operativo, el modelo de comisiones y la renovación mensual del servicio.</span>
                </label>
                <button type="button" onClick={handlePartnerCheckout} className="btn-primary w-full justify-center" disabled={legalState.processing} data-testid="partner-legal-confirm-buy-button">{legalState.processing ? 'Preparando checkout...' : 'Aceptar contrato y comprar ahora'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnersPage;